import type { IAppActivitySource, IStorageAdapter } from "../utils";
import type { IAssistant } from "./assistant";
import type { IMessageInfo } from "./generated-video";
import type { IGenerationFailureErrorMetadata } from "./generation-failure";
import type { IModel } from "./models";
import type { IPiiSpan } from "./pii-span";
import type { IPresentationThemePalette } from "./presentation-theme";
import type { TSkillScriptRunRecord } from "./skill-sandbox";
import type { TSkillActivationRecord } from "./skills";
import type {
  IAttachmentTextExtractionWarning,
  ITextExtractionWarning,
} from "./text-extraction";
import type { ITokenBudgetErrorMetadata } from "./token-budget";

// NOTE: NSAI-5678 — which side of the assistant / regular split a chat-list
// request asks for. Absent means every chat, which is what folders, archive and
// search want and what an older client keeps sending.
export enum EChatListKind {
  REGULAR = "regular",
  ASSISTANT = "assistant",
}

// NOTE: NSAI-5678 — the assistant meta the sidebar needs to file an assistant
// chat under its assistant and label the group. Resolved server-side so a chat
// whose assistant has since been unshared or deleted still shows its name. No
// avatar on purpose — those are data URLs of up to 512KB and would repeat on
// every row; the clients look them up in the assistants cache they already
// hold and fall back to the generic assistant glyph.
export interface IChatListAssistant {
  uuid: string;
  name: string;
}

export interface IChatListObject {
  uuid: string;
  title: string;
  isArchived: boolean;
  isPinned: boolean;
  pinnedAt: string | null;
  folderId: string | null;
  createdAt: string;
  // NOTE: NSAI-4559 — latest message timestamp; the sidebar sorts and
  // date-groups by this instead of createdAt. Optional so a client running
  // against an older server still renders (grouping falls back to createdAt).
  lastMessageAt?: string | null;
  telegramChatId: number | null;
  teamsConversationId: string | null;
  slackChannelId: string | null;
  // NOTE: Boolean flag only — the server deliberately never sends the WhatsApp
  // phone number on chat payloads (it is directly-identifying PII).
  isWhatsappChat: boolean;
  // NOTE: NSAI-4950 — boolean flag only; the Matrix room id stays server-side
  // and the clients only need to know the chat is read-only here.
  isMatrixChat: boolean;
  hasActiveShare: boolean;
  // NOTE: NSAI-5678 — the chat's current assistants in slot order; empty for a
  // model chat. Optional so a client against an older server still renders
  // (every chat then counts as a regular one).
  assistants?: IChatListAssistant[];
}

export interface IGetChatListParams {
  limit: number;
  offset: number;
  search?: string;
  folderId?: string;
  // NOTE: NSAI-5678 — omit to list every chat regardless of kind.
  kind?: EChatListKind;
}

export interface ISelectPinnedChatsProps {
  chats: IChatListObject[];
}

export enum ESearchMode {
  DEEP = "deep",
  WEB = "web",
  NONE = "none",
  // NOTE: NSAI-6539 (contract C5) — "Auto decides". Sent only while the Auto
  // row is selected and the user has not touched the search toggle
  // (resolveAutoSendModes); the server treats it as NONE on any other turn.
  AUTO = "auto",
}

// NOTE: NSAI-6431 — scoping state of a deep research run, persisted on its
// assistant message (`deepResearchRun`). `awaiting_clarification` means the
// message asked the questions in `questions`; the user's next deep-mode
// message is the answer and resumes the run instead of starting a new one.
export enum EDeepResearchRunStatus {
  AWAITING_CLARIFICATION = "awaiting_clarification",
  RESEARCHING = "researching",
  COMPLETED = "completed",
  // NOTE: NSAI-7631 — a user Stop ended the run. Nothing renders off it yet:
  // the view reads the stop from the progress record's phase.
  STOPPED = "stopped",
}

export enum EDeepResearchQuestionKind {
  TEXT = "text",
  CHOICE = "choice",
}

export interface IDeepResearchClarificationQuestion {
  id: string;
  text: string;
  kind: EDeepResearchQuestionKind;
  options?: string[];
}

export interface IDeepResearchRun {
  status: EDeepResearchRunStatus;
  questions?: IDeepResearchClarificationQuestion[];
  verification?: string;
  brief?: string;
  resumedFromMessageUuid?: string;
}

// NOTE: NSAI-6431 — where the user is in the clarification panel. Pure data so
// web and mobile share the stepping rules (`applyClarificationStepAction`)
// and keep only a thin React wrapper each.
export interface IClarificationStepsState {
  stepIndex: number;
  // NOTE: Keyed by question id.
  answers: Record<string, string>;
  // NOTE: Choice questions answered through "Other" (a typed answer).
  otherSelectedIds: string[];
}

export enum EClarificationStepAction {
  SET_ANSWER = "set_answer",
  SELECT_OPTION = "select_option",
  SELECT_OTHER = "select_other",
  GO_BACK = "go_back",
  GO_NEXT = "go_next",
}

export type TClarificationStepAction =
  | {
      type: EClarificationStepAction.SET_ANSWER;
      questionId: string;
      answer: string;
    }
  | { type: EClarificationStepAction.SELECT_OPTION; option: string }
  | { type: EClarificationStepAction.SELECT_OTHER }
  | { type: EClarificationStepAction.GO_BACK }
  | { type: EClarificationStepAction.GO_NEXT };

export interface IClarificationStepsView {
  currentQuestion: IDeepResearchClarificationQuestion | undefined;
  answer: string;
  isOtherSelected: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  canSubmit: boolean;
}

// NOTE: NSAI-5659 — which flow asked: a Deep Research run (the answer is sent
// in deep mode) or an Auto chat about to build an HTML page (the answer is an
// ordinary message on the chat's model). One panel serves both.
export enum EPendingClarificationKind {
  DEEP_RESEARCH = "deep_research",
  HTML_PAGE = "html_page",
}

export interface IPendingClarification {
  messageUuid: string;
  questions: IDeepResearchClarificationQuestion[];
  kind: EPendingClarificationKind;
}

// NOTE: NSAI-5659 (part 2) — the batched clarification an Auto chat asked
// before building an HTML page, on the assistant message that asked
// (`htmlPageClarification`). Same question shape as Deep Research.
export enum EHtmlPageClarificationStatus {
  AWAITING_ANSWERS = "awaiting_answers",
}

export interface IHtmlPageClarification {
  status: EHtmlPageClarificationStatus;
  questions: IDeepResearchClarificationQuestion[];
}

// NOTE: NSAI-5659 (part 2) — what a client reports when a generated page
// throws while rendering (socket event HTML_PAGE_RENDER_ERROR_EVENT).
export interface IHtmlPageRenderErrorPayload {
  chatUuid: string;
  messageUuid: string;
  fileUuid: string;
  error: string;
}

export enum EReasoningMode {
  THINKING = "thinking",
  FAST = "fast",
  // NOTE: NSAI-6539 (contract C5) — "Auto decides" from the turn plan. A
  // request value only: never offered in the reasoning selector.
  AUTO = "auto",
}

// NOTE: NSAI-6539 — the capabilities Auto can switch on for a turn. The values
// are the server's EAutoCapability keys.
export enum EAutoCapability {
  WEB_SEARCH = "webSearch",
  DEEP_RESEARCH = "deepResearch",
  IMAGE_GENERATION = "imageGeneration",
  DOCUMENT_GENERATION = "documentGeneration",
  DECK = "deck",
  HTML_PAGE = "htmlPage",
  KNOWLEDGE_RETRIEVAL = "knowledgeRetrieval",
  FILE_ANALYSIS = "fileAnalysis",
  CONNECTOR = "connector",
}

// NOTE: NSAI-6542 — how an Auto confirmation card resolved.
export enum EAutoConfirmationOutcome {
  ACCEPTED = "accepted",
  DECLINED = "declined",
  DISMISSED = "dismissed",
}

// NOTE: NSAI-6542 — `autoConfirmationRequest`: the server paused an Auto turn
// before a costly capability. `uuid` is the assistant message it belongs to;
// `confirmationId` names this one card.
export interface IAutoConfirmationRequestPayload {
  chatUuid: string;
  uuid: string;
  confirmationId: string;
  capability: EAutoCapability;
  modelName: string;
  // NOTE: ISO timestamp; past it the turn answers normally on its own.
  expiresAt: string;
}

// NOTE: NSAI-6542 — `autoConfirmationResolved`: the card is answered, timed
// out or stopped. Every open tab clears it.
export interface IAutoConfirmationResolvedPayload {
  chatUuid: string;
  uuid: string;
  confirmationId: string;
  capability: EAutoCapability;
  outcome: EAutoConfirmationOutcome;
}

// NOTE: NSAI-6542 — `autoConfirmationResponse`: accept, or "answer normally".
export interface ISendAutoConfirmationResponseParams {
  chatUuid: string;
  confirmationId: string;
  isAccepted: boolean;
}

export type TReasoningLabelKey =
  | "input.reasoning_thinking"
  | "input.reasoning_fast"
  | "autoModel.composer.auto";

// NOTE: NSAI-6541 — how the user overrode a capability Auto enabled:
// `rerun_without` from the answer's one-click override, `disabled` by
// switching it off in the composer. The server counts it on
// chat_auto_capability_overridden_total.
export enum EAutoCapabilityOverrideAction {
  RERUN_WITHOUT = "rerun_without",
  DISABLED = "disabled",
}

export interface IAutoCapabilityOverride {
  // NOTE: The Auto answer whose capabilities the user overrode.
  messageUuid: string;
  capabilities: EAutoCapability[];
  action: EAutoCapabilityOverrideAction;
}

export interface IReasoningModeOption {
  mode: EReasoningMode;
  labelKey: TReasoningLabelKey;
}

export interface IReasoningItem {
  isReasoningModel: boolean;
}

export interface IIsReasoningApplicableProps {
  selectedModels: IReasoningItem[];
  selectedAssistants: IReasoningItem[];
}

export enum EMessageRole {
  SYSTEM = "system",
  SYSTEM_ERROR = "system_error",
  USER = "user",
  ASSISTANT = "assistant",
}

export enum EGenerationType {
  Image = "image",
  // NOTE: NSAI-6621 — a turn answered by a video-generation model.
  Video = "video",
  Text = "text",
  Summary = "summary",
  BranchContext = "branch_context",
}

export enum EToolApprovalState {
  REQUESTED = "requested",
  APPROVED = "approved",
  DENIED = "denied",
}

export enum ESelectionKind {
  MODELS = "models",
  ASSISTANTS = "assistants",
}

export enum EModelChangeKind {
  SWITCHED_TO = "switched_to",
  SWITCHED_BACK = "switched_back",
  CHANGED = "changed",
  ADDED = "added",
  REMOVED = "removed",
  UPDATED = "updated",
  CONTINUING_WITH = "continuing_with",
}

export interface IIndexedSelectionItem {
  uuid: string;
  index: number;
}

export interface IIndexedSelectionItemModel extends IIndexedSelectionItem {
  name: string;
  title: string;
  provider: string;
  hostingProvider: string;
  // NOTE: NSAI-7765 — true on the Auto (router) row. Sent by the server on a
  // chat's `currentModels` and copied from the catalog on a pick, so a chat
  // whose Auto the organization turned off is told apart from one whose model
  // was deleted, after Auto has left the model list.
  isRouterModel?: boolean;
}

export interface IIndexedSelectionItemAssistant extends IIndexedSelectionItem {
  name: string;
  avatar: string | null;
  isDeleted?: boolean;
}

export interface IAssistantMeta {
  name: string;
  avatar: string | null;
  isDeleted?: boolean;
}
export enum EKnowledgeSourceType {
  KNOWLEDGE = "knowledge",
  CHAT_FILE = "chat_file",
  ASSISTANT_FILE = "assistant_file",
}

// NOTE: Cross-chat context provenance (NSAI-3501) — a prior chat whose
// user-memory facts contributed context to the current answer. Surfaced under
// the assistant message so the user can trace an injected value and open the
// source chat.
export interface IContextChatSource {
  chatUuid: string;
  title: string | null;
  // NOTE: ISO date (no time) of the source chat, for a stable, locale-free label.
  matchedAt: string;
  // NOTE: Recalled fact text (NSAI-3501) — used to match the answer's actual
  // value to the chat that declared it, so only the chat the answer was based on
  // is shown. Not rendered directly.
  content?: string;
}

export enum EMessageFileSource {
  USER_UPLOAD = "user_upload",
  TOOL_OUTPUT = "tool_output",
  AI_GENERATED = "ai_generated",
  ASSISTANT = "assistant",
}

// NOTE: Represents a file attachment on a message returned from the chat history API
export interface IMessageFileAttachment {
  uuid: string;
  name: string;
  type: string;
  source?: EMessageFileSource;
  // NOTE: Base64 data URL for image files (e.g. "data:image/png;base64,...")
  imageBase64?: string | null;
  // NOTE: NSAI-6974 — PII masking kept this image from the model; the
  // organization's image description model described it instead, and personal data in
  // that description was masked. The chat warns the user on the image.
  isPiiFoundInImage?: boolean;
  // NOTE: NSAI-7345 — this PDF yielded too little text for its page count, so
  // the model read only part of it. The chat warns under the attachments.
  textExtractionWarning?: ITextExtractionWarning;
}

// NOTE: NSAI-6974 — payload of PII_IMAGE_MASKED_SOCKET_EVENT: the user
// message and which of its images had personal data masked.
export interface IPiiImageMaskedPayload {
  chatUuid: string;
  messageUuid: string;
  fileUuids: string[];
}

export interface IMarkPiiFoundInImagesProps {
  message: IMessage;
  fileUuids: readonly string[];
}

export interface IMarkTextExtractionWarningsProps {
  message: IMessage;
  files: readonly IAttachmentTextExtractionWarning[];
}

export interface IMessageError {
  content: string;
  code?: string;
  tokenBudget?: ITokenBudgetErrorMetadata;
  // NOTE: NSAI-5810 — present only when `code` is GENERATION_FAILURE: the turn
  // failed because our own code faulted, and this carries what the failure card
  // needs that the content cannot (retryability, and the engine wording that
  // belongs in a collapsed disclosure rather than in the sentence the user
  // reads).
  generationFailure?: IGenerationFailureErrorMetadata;
}

export interface IToolFile {
  name: string;
  type: string;
  size?: number;
  // NOTE: File UUID from message_files table — used by download API.
  uuid?: string;
}

// NOTE: NSAI-5656 — one HTML page the assistant produced, as the preview panel
// lists it: a version the user can switch to, download or open standalone.
export interface IHtmlPreviewVersion {
  fileUuid: string;
  fileName: string;
  messageUuid: string;
  createdAt: string;
}

export interface IAreHtmlPreviewVersionsEqualProps {
  first: IHtmlPreviewVersion[];
  second: IHtmlPreviewVersion[];
}

export interface IMessageDeltaPayload {
  uuid: string;
  delta: string;
  chatUuid?: string;
}

export interface IMessageContentUpdatePayload {
  uuid: string;
  content: string;
  chatUuid?: string;
}

export interface IToolCallPayload {
  uuid: string;
  toolName: string;
  args: Record<string, unknown>;
  chatUuid?: string;
  // NOTE: NSAI-5473 — present on a REAL tool call only. The synthetic progress
  // frames (`_progressStatus`, `_completed`, the HTML / deep-search indicators)
  // carry none, which is what keeps them out of the step transcript.
  toolCallId?: string;
}

// NOTE: NSAI-5473 — where one tool call of an assistant turn stands. RUNNING
// until the server says it ended; one still RUNNING on a finished message was
// interrupted (the turn was stopped, or it failed around it).
export enum EToolStepStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
  DENIED = "denied",
}

// NOTE: NSAI-5473 — one line of the step transcript. `args` are the short
// scalars the tool label interpolates, not the call's full payload.
export interface IMessageToolStep {
  id: string;
  name: string;
  args: Record<string, unknown>;
  status: EToolStepStatus;
}

export interface IToolCallEndPayload {
  uuid: string;
  toolCallId: string;
  toolName: string;
  status: Exclude<EToolStepStatus, EToolStepStatus.RUNNING>;
  chatUuid?: string;
}

export interface IToolApprovalRequestPayload {
  uuid: string;
  approvalId: string;
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  chatUuid?: string;
  // NOTE: NSAI-7877 — the server's auto-decline window in whole minutes, so the
  // card's hint can quote it. Absent from an older server, which falls back to
  // the hint without a number.
  autoDeclineAfterMinutes?: number;
}

export interface IToolApprovalRespondedPayload {
  uuid: string;
  approvalId: string;
  approved: boolean;
  reason?: string;
  chatUuid?: string;
}

export interface IToolFilePayload {
  uuid: string;
  chatUuid?: string;
  toolName: string;
  file: IToolFile;
}

// NOTE: Emitted when a tool-generated file (e.g. a presentation deck) fails to
// upload to S3 after retries, so no `toolFile` will follow. Clients surface this
// to the user so a failed attachment isn't silent (NSAI-3622 / NSAI-3613).
export interface IToolFileErrorPayload {
  uuid: string;
  chatUuid?: string;
  toolName: string;
  file: Pick<IToolFile, "name" | "type">;
}

export interface IReasoningStepPayload {
  uuid: string;
  reasoning: string;
  chatUuid?: string;
}

export interface IStreamErrorPayload {
  uuid: string;
  error: string;
  chatUuid?: string;
}

export interface IStreamFinishPayload {
  uuid: string;
  reason: string;
  chatUuid?: string;
}

// NOTE: NSAI-2871 — one retrieved source as the server numbered it for the run:
// `n` is the citation number the answer's inline `[n]` and its Sources section
// use, so the card can show which page each citation points at.
export interface ISearchQuerySource {
  n: number;
  url: string;
  title: string;
}

// NOTE: NSAI-6435 — the stage a Deep Research v2 run is in. Mirrors the
// server's EDeepResearchPhase.
export enum EDeepResearchPhase {
  // NOTE: NSAI-6431 — Step 0: whether to ask the user first, and the brief.
  SCOPING = "scoping",
  // NOTE: NSAI-6431 — the run stopped to ask; the user's answer starts the
  // next one.
  AWAITING_CLARIFICATION = "awaiting_clarification",
  PLANNING = "planning",
  SEARCHING = "searching",
  RESEARCHING = "researching",
  WRITING = "writing",
  VERIFYING = "verifying",
  COMPLETED = "completed",
  STOPPED = "stopped",
  // NOTE: NSAI-7632 — the run ended on an error; the message's error says
  // why.
  FAILED = "failed",
}

// NOTE: NSAI-6435 — what limited a run. Mirrors the server's
// EResearchBudgetLimit.
export enum EResearchBudgetLimit {
  TOKENS = "tokens",
  TIME = "time",
  ROUNDS = "rounds",
}

// NOTE: NSAI-6435 — a search-provider condition that cost the run sources.
// Mirrors the server's EResearchSearchCondition.
export enum EResearchSearchCondition {
  QUOTA_EXHAUSTED = "quota_exhausted",
  PROVIDER_UNREACHABLE = "provider_unreachable",
}

export interface IResearchRunSpend {
  tokensUsed: number;
  tokenBudget: number;
  estimatedCostUsd: number | null;
}

// NOTE: NSAI-6435 — the report check's result: `supported` of `judged`
// claims, and whether the score fell under the threshold.
export interface IDeepResearchClaimsSummary {
  supported: number;
  judged: number;
  isLowConfidence: boolean;
}

// NOTE: NSAI-6435 — a Deep Research v2 run's progress, persisted on the
// assistant message and sent on every progress event. Times are
// milliseconds after `startedAt`. Mirrors the server's
// IDeepResearchProgressSnapshot.
export interface IDeepResearchProgressSnapshot {
  startedAt: string;
  phase: EDeepResearchPhase;
  round: number;
  maxRounds: number;
  plannedAtMs?: number;
  writingAtMs?: number;
  verifyingAtMs?: number;
  finishedAtMs?: number;
  spend?: IResearchRunSpend;
  budgetLimit?: EResearchBudgetLimit;
  searchCondition?: EResearchSearchCondition;
  // NOTE: The report cites the sources numbered 1..citedSourceCount.
  citedSourceCount?: number;
  claims?: IDeepResearchClaimsSummary;
}

export interface IDeepSearchProgressPayload {
  uuid: string;
  status: string;
  message: string;
  query?: string;
  queryIndex?: number;
  totalQueries?: number;
  completedCount?: number;
  chatUuid?: string;
  // NOTE: NSAI-6435 — Deep Research v2 only: the round, the cost so far, the
  // report check's progress and the run's progress record.
  round?: number;
  spend?: IResearchRunSpend;
  claimsChecked?: number;
  claimsTotal?: number;
  researchProgress?: IDeepResearchProgressSnapshot;
}

export interface IWebSearchProgressPayload {
  uuid: string;
  status: string;
  message: string;
  query?: string;
  queryIndex?: number;
  totalQueries?: number;
  completedCount?: number;
  chatUuid?: string;
}

export interface IMessagePreparationProgressPayload {
  uuid: string;
  status: string;
  message: string;
  chatUuid?: string;
}

export interface IModelForHistory {
  uuid: string;
  title: string;
  // NOTE: Optional region/hosting info used by UI for data-sovereignty indicators
  // (e.g. the moderation fallback badge). Not populated on legacy message payloads.
  region?: string;
  hostingProvider?: string;
}

export interface IMessage {
  uuid: string;
  chatUuid: string;
  role: EMessageRole;
  groupId?: string;
  slotIndex?: number;
  content: string;
  files: IMessageFileAttachment[];
  createdAt: string;
  updatedAt: string;
  parentId?: string | null;
  childrenIds?: string[] | null;
  model: IModelForHistory;
  provider: string;
  done: boolean;
  generationType: EGenerationType;
  error?: IMessageError | null;
  tool?: IMessageToolCall;
  // NOTE: NSAI-5473 — every tool call the turn made, in order, with how each
  // ended. `tool` above is only the one running now; this is the transcript.
  toolSteps?: IMessageToolStep[];
  toolApprovals?: Array<{
    tool: {
      name: string;
      args: Record<string, unknown>;
    };
    toolApproval: {
      approvalId: string;
      toolCallId: string;
      state: EToolApprovalState;
      approved?: boolean;
      reason?: string;
      // NOTE: NSAI-7877 — see IToolApprovalRequestPayload.autoDeclineAfterMinutes.
      autoDeclineAfterMinutes?: number;
    };
  }>;
  toolFiles?: IToolFile[];
  reasoning?: string;
  // NOTE: User-selected reasoning mode at send time (thinking | fast). Only set
  // on input (user) messages sent with a reasoning-capable model; null/undefined
  // otherwise. The web app derives a chat's active reasoning mode from its
  // latest input message so the selector restores on revisit/reload, and the
  // analytics message-details modal resolves an AI response's mode from its
  // parent user message.
  reasoningMode?: EReasoningMode | null;
  // NOTE: NSAI-3308 — set on an INPUT (user) message only. It is the id the
  // transport generated for the send that produced this row, and it is what lets
  // a composer clear its pinned skills on CONFIRMED PERSISTENCE rather than on a
  // socket acknowledgement, which only proves the gateway received the payload.
  clientRequestId?: string | null;
  // NOTE: The server-normalized skills the user pinned to this user message. A
  // manual resend replays these, not whatever the composer holds now.
  requestedSkillUuids?: string[] | null;
  // NOTE: NSAI-5925 — set on an INPUT (user) message only: the skills this message
  // ASKED FOR and did not get, because it was already at the per-message cap. Its
  // sibling above records what was applied; without this one, reading the
  // conversation back gives no way to tell that a sixth skill was requested at all
  // — the `/name` is still in the body, but a mention in body text is
  // indistinguishable from ordinary prose.
  refusedSkillNames?: string[] | null;
  // NOTE: Server-owned activation records for an assistant message. Durable, so
  // the same timeline survives a refresh; the live `skillActivations` event
  // carries the same records for the same message uuid.
  skillActivations?: TSkillActivationRecord[];
  // NOTE: NSAI-3304 — server-owned sandboxed script-run records for an assistant
  // message. Durable, so the same block survives a refresh; the live
  // `skillScriptRun` event carries the same record for the same run uuid.
  skillScriptRuns?: TSkillScriptRunRecord[];
  // NOTE: NSAI-6156 — set ONLY by the reviewer-facing reads (Security Analytics
  // message context / thread window, the admin transcript) on a project that
  // anonymizes PII, and only for a message that actually carried some. Each
  // entry locates a value inside THIS message's `content` and names the token
  // the model received in its place, so the reviewer can see what was masked
  // rather than reading a restored body that looks untouched. The chat
  // transcript itself never sends it, which is why the chat render path is
  // unaffected.
  piiSpans?: IPiiSpan[] | null;
  streamError?: string;
  streamFinished?: boolean;
  deepSearchQueries?: Array<{
    query: string;
    status: string;
    sources?: ISearchQuerySource[];
    // NOTE: NSAI-2873 — the research round of a deep run's later topic;
    // absent for the planned queries (round one).
    round?: number;
    // NOTE: NSAI-6435 — when a v2 run started and finished this search or
    // topic, in milliseconds after `deepResearchProgress.startedAt`.
    startedAtMs?: number;
    finishedAtMs?: number;
  }>;
  deepSearchProgress?: {
    status: string;
    message: string;
    query?: string;
    queryIndex?: number;
    totalQueries?: number;
    completedCount?: number;
    round?: number;
    // NOTE: NSAI-6435 — live only: the cost so far and the report check's
    // progress.
    spend?: IResearchRunSpend;
    claimsChecked?: number;
    claimsTotal?: number;
  };
  // NOTE: NSAI-6435 — a Deep Research v2 run's progress record: present on
  // v2 answers, from history and `messageUpdated`, and refreshed by each
  // progress event. Its absence marks a run the earlier progress card shows.
  deepResearchProgress?: IDeepResearchProgressSnapshot | null;
  webSearchQueries?: Array<{
    query: string;
    status: string;
    urls?: string[];
    sources?: ISearchQuerySource[];
    results?: Array<{
      title: string;
      url: string;
      favicon?: string;
    }>;
  }>;
  webSearchProgress?: {
    status: string;
    message: string;
    query?: string;
    queryIndex?: number;
    totalQueries?: number;
    completedCount?: number;
  };
  knowledgeSources?: Array<{
    // NOTE: content is only present during live streaming (websocket).
    // After page refresh, sources are loaded from DB which omits content.
    content?: string;
    score: number;
    fileName: string;
    fileUuid: string;
    // NOTE: Identifies the origin so the correct download API is used.
    // "knowledge" → knowledge base, "chat_file" / "assistant_file" → message_files.
    source?: EKnowledgeSourceType;
  }>;
  // NOTE: Cross-chat context provenance (NSAI-3501) — prior chats whose
  // user-memory facts fed this answer, so the UI can show WHICH chat an
  // injected value came from and link back to it.
  contextChatSources?: IContextChatSource[];
  // NOTE: True when the assistant response was produced by the probe-rerouting
  // fallback model (user prompt failed probe validation, organization opted for
  // rerouting instead of blocking). UI renders a "moderation fallback" badge.
  isReroutedByProbe?: boolean;
  // NOTE: True when the assistant response was produced by the org's PII
  // trusted model (entities were the subject of the query; anonymization is
  // off for this chat). UI renders a "trusted model" badge.
  isRoutedByPiiIntent?: boolean;
  // NOTE: NSAI-5876 — true when the assistant response ran on the model picked
  // by presentation best-model routing (NSAI-4157) instead of the user's own
  // selection. Read by the model-change informer, which narrates the user's
  // selection changes and must ignore turns the server routed.
  isRoutedForPresentation?: boolean;
  // NOTE: NSAI-4948 (contract C2) — true when Auto (the router model) picked the
  // model this reply ran on. `model` then names the concrete model that
  // answered, and the UI renders the "Answered by … · chosen by Auto" badge.
  isAutoRouted?: boolean;
  // NOTE: NSAI-6539 — the capabilities Auto switched on for this turn (REST
  // history and the socket payloads); null / absent on every other turn.
  autoCapabilities?: EAutoCapability[] | null;
  // NOTE: NSAI-7774 — the image model the reply's `generateImage` calls ran on
  // (REST history and the final `messageUpdated`); null / absent when the reply
  // drew no image. `model` stays the model that wrote the reply, so the Auto
  // badge names both.
  imageModel?: IModelForHistory | null;
  // NOTE: NSAI-6541 — the reasoning mode Auto chose for this reply, read by
  // the answer line. Not stamped yet (autoCapabilities carries no reasoning),
  // so the line names no "Thinking" until the server sends it.
  autoReasoningMode?: EReasoningMode | null;
  preparationProgress?: {
    status: string;
    message: string;
  };
  // NOTE: NSAI-5681 — "connect this app" cards raised on an assistant reply by
  // the server's requestIntegrationConnection tool. Durable (the history read
  // returns them) and live (the `integrationConnectPrompt` socket event carries
  // one at a time for the same message uuid), so the clients merge by provider
  // instead of appending.
  integrationConnectPrompts?: IIntegrationConnectPrompt[];
  // NOTE: NSAI-5611 — the results of the `runAgentAndWait` chat tool on this
  // assistant reply, one per agent run, rendered as run cards under the answer.
  // Typed `unknown` because each entry is server-owned and the tool returns a
  // plain string on a refusal: read it only through `readAgentRunResults`,
  // which keeps the entries that pass `isRunAgentAndWaitResult`.
  agentRunResults?: unknown;
  // NOTE: NSAI-5613 — the answers the `askAssistant` chat tool got on this
  // reply, each attributed to the assistant that gave it. `unknown` for the
  // same reason as `agentRunResults`: read it only through
  // `readAssistantAnswers`.
  assistantAnswers?: unknown;
  // NOTE: NSAI-6431 — present on deep research answers; drives the
  // clarification card while the run is awaiting answers.
  deepResearchRun?: IDeepResearchRun | null;
  // NOTE: NSAI-5659 — present on the Auto answer that asked a page's
  // questions; drives the same clarification panel.
  htmlPageClarification?: IHtmlPageClarification | null;
  // NOTE: NSAI-6925 — server-owned notes on how this assistant message was
  // produced (e.g. a video length the model could not render was adjusted).
  info?: IMessageInfo | null;
}

// NOTE: NSAI-5681 — one "connect this app" prompt on an assistant message.
// `provider` is an EIntegrationProvider slug the deployment can connect, and it
// is the whole record: WHICH account gets connected is decided on the
// provider's own OAuth screen behind the card's button, so no account detail is
// collected, carried or rendered. Rows written before that simplification may
// still hold an extra `accountHint` key in jsonb; nothing reads it.
// NOTE: NSAI-5681 — how a "connect this app" card ended. Every card reaches
// exactly one of these, and the record is what the four card styles render
// from — so the outcome survives a reload, matches on web and mobile, and does
// not have to be re-derived from timestamps on each client.
export enum EIntegrationConnectResolution {
  // NOTE: A connection for this app landed after the card was raised. The
  // server verifies this against the integrations table before recording it,
  // so a stale tab cannot mark a card connected.
  CONNECTED = "connected",
  // NOTE: A connect was started from the card and came back with nothing —
  // rejected consent, failed callback, abandoned provider tab. The only
  // resolution that still allows another attempt, until the next message.
  FAILED = "failed",
  // NOTE: The user closed the card.
  DISMISSED = "dismissed",
  // NOTE: The user moved on: a later message in the chat closed every card
  // still open (or failed) on the replies before it.
  TERMINATED = "terminated",
}

export interface IIntegrationConnectPrompt {
  provider: string;
  createdAt: string;
  // NOTE: Absent while the card is still open. The record is kept on the
  // message rather than removed, so the server's per-message dedupe still
  // recognises the app and the model cannot redraw a card the user answered.
  resolution?: EIntegrationConnectResolution;
  resolvedAt?: string;
  // NOTE: Recorded from the SERVER's integration row when the card resolves as
  // connected, so "Connected as mohd+1@nodeshift.com" stays true in the
  // transcript even after that account is later disconnected.
  connectedAccountTitle?: string;
  // NOTE: NSAI-8152 — set only on an `mcps` card that names ONE server. Its
  // `url` is part of the card's identity (two servers are two cards), and it
  // turns the card from "Connect MCP" into "<server> — Review and add".
  mcpServer?: IIntegrationConnectPromptMcpServer;
}

// NOTE: NSAI-8152 — where the server on an MCP card came from. Mirrors the
// server's EMcpServerSuggestionSource; the values are the contract.
export enum EMcpServerSuggestionSource {
  // NOTE: The organization's managed catalog — opens the form's organization
  // picker on that server.
  ORGANIZATION = "organization",
  // NOTE: A vendor's official hosted server from the server-side registry.
  KNOWN = "known",
  // NOTE: A URL the user typed into their own message.
  USER = "user",
}

// NOTE: NSAI-8152 — what the card says about signing in. Mirrors the server's
// EMcpServerSuggestionAuth.
export enum EMcpServerSuggestionAuth {
  OAUTH = "oauth",
  API_KEY = "api_key",
  NONE = "none",
  ORGANIZATION = "organization",
  UNKNOWN = "unknown",
}

// NOTE: NSAI-8152 — the snapshot the server stored when it offered the server.
// `transport` / `authMode` / `authHeaderName` prefill the connect form and are
// set only for a known server; they are plain strings here because a card
// outlives enum changes, and buildMcpConnectPrefill checks them.
export interface IIntegrationConnectPromptMcpServer {
  title: string;
  url: string;
  source: EMcpServerSuggestionSource;
  auth: EMcpServerSuggestionAuth;
  knownServerKey?: string;
  transport?: string;
  authMode?: string;
  authHeaderName?: string;
  isOAuthClientRequired?: boolean;
  docsUrl?: string;
}

export interface IResolveIntegrationConnectPromptPayload {
  chatUuid: string;
  messageUuid: string;
  provider: string;
  // NOTE: NSAI-8152 — identifies an MCP card that names a server.
  mcpServerUrl?: string;
  resolution: EIntegrationConnectResolution;
}

export interface IIntegrationConnectPromptPayload {
  uuid: string;
  prompt: IIntegrationConnectPrompt;
  chatUuid?: string;
  // NOTE: NSAI-5681 — the transcript line this resolution wrote, travelling
  // with the card it explains. A resolved card changes state and the chat gains
  // a line saying why; sending them separately meant the note only appeared on
  // the next history read (reported: "the note shows only after a refresh").
  // Absent when the outcome wrote no note — a card resolved silently because an
  // earlier one in the same chat already carried the line for this outcome.
  note?: IMessage;
}

export interface IMessageWithTempId extends IMessage {
  tempId?: string;
}

export interface IHasStatus {
  status: string;
}

export interface IChat {
  uuid: string;
  title: string;
  isArchived: boolean;
  isPinned: boolean;
  messages?: IMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface IChatTitleGenerated {
  chatUuid: string;
  title: string;
}

export interface IChatListResponse {
  data: IChatListObject[];
  hasNextPage: boolean;
}

export interface IFetchChatListProps {
  pageParam: number;
  searchQuery?: string;
}

export interface IUseChatListProps {
  searchQuery?: string;
}

export interface IChangedModelMessage {
  id: string;
  model: IModelForHistory;
  isOriginalModel: boolean;
  showResetModelButton: boolean;
}

export interface IChatSettings {
  isWebSearchEnabled: boolean;
  isDeepResearchEnabled: boolean;
  isPiiAnonymizationEnabled: boolean;
  // NOTE: Read-only flag the backend adds to the project chat-settings GET
  // response so the chat composer can show/hide the "Apply brand theme" toggle.
  // Optional because org-settings update payloads (web & mobile) reuse this
  // type without setting it.
  isPresentationThemeEnabled?: boolean;
  // NOTE: NSAI-5121 — the organization's brand palette, so a diagram drawn in
  // the transcript can be painted in the company's colours. Deliberately NOT
  // gated on the presentation builder the way `isPresentationThemeEnabled` is:
  // a diagram in a chat message is not a deck, so an org whose builder is off
  // should still get its own colours. Null when the org has no enabled theme,
  // and absent on the org-settings update payloads that reuse this type.
  diagramBrandPalette?: IPresentationThemePalette | null;
  // NOTE: NSAI-6543 — the org's Auto controls. Optional: absent on the
  // org-settings payloads that reuse this type, and from a server that predates
  // them (both read as "available, not the default").
  isAutoModeEnabled?: boolean;
  isAutoModeDefault?: boolean;
}

export interface IUpdateChatTitlePayload {
  uuid: string;
  title: string;
}

export interface IChatHistoryResponse {
  data: IMessage[];
  hasNextPage: boolean;
}

export interface IChatHistoryRequest {
  chatId: string;
  limit: number;
  offset: number;
}

// NOTE: chatUuid comes from the route parameter, not the body
export interface IBranchChatRequest {
  messageUuid: string;
  modelUuid?: string;
}

export interface IBranchChatResponse {
  chatUuid: string;
}

// NOTE: NSAI-6295 — the trusted model a PII-intent-routed chat is locked to.
// `isDeleted` is true when the model has since been soft-deleted.
export interface IChatPiiTrustedModel {
  uuid: string;
  name: string;
  title: string;
  provider: string;
  hostingProvider: string;
  region?: string;
  isDeleted?: boolean;
  // NOTE: NSAI-7404 — false when the locked model can no longer answer this
  // chat (deleted, disabled by an admin, or hidden for the organization or the
  // chat's project). Every new message is then refused, so clients show a
  // persistent notice instead of a send that fails again. Absent = available.
  isAvailable?: boolean;
}

// NOTE: NSAI-7404 — a send the server refused with
// PII_TRUSTED_MODEL_UNAVAILABLE: which chat, and when (`Date.now()`). The time
// is compared with the chat query's `dataUpdatedAt`, so only a payload fetched
// after the refusal can overrule it.
export interface IPiiTrustedUnavailableRefusal {
  chatUuid: string;
  refusedAt: number;
}

export interface IChatByUuidResponse {
  uuid: string;
  title: string;
  isArchived: boolean;
  // NOTE: NSAI-6473 — needed by the chat page's header, which offers the same
  // Pin / Unpin action the sidebar row does.
  isPinned: boolean;
  folderId: string | null;
  originalModels: IIndexedSelectionItemModel[];
  currentModels: IIndexedSelectionItemModel[];
  currentAssistants: IIndexedSelectionItemAssistant[];
  createdAt: string;
  updatedAt: string;
  searchMode: ESearchMode;
  parentChatUuid: string | null;
  parentChatTitle: string | null;
  branchedFromMessageUuid: string | null;
  telegramChatId: number | null;
  teamsConversationId: string | null;
  slackChannelId: string | null;
  // NOTE: Boolean flag only — the server deliberately never sends the WhatsApp
  // phone number on chat payloads (it is directly-identifying PII).
  isWhatsappChat: boolean;
  // NOTE: NSAI-4950 — boolean flag only; the Matrix room id stays server-side
  // and the clients only need to know the chat is read-only here.
  isMatrixChat: boolean;
  // NOTE: NSAI-6295 — set once PII Intent Routing locks the chat to the
  // organization's trusted model. The server then serves every turn from this
  // model whatever `currentModels` says, so clients show it in the composer and
  // keep the picker read-only. Optional: absent from servers that predate it.
  piiTrustedModel?: IChatPiiTrustedModel | null;
}

export interface IUpdateChatCurrentModelParams {
  uuid: string;
  models?: IIndexedSelectionItem[];
  assistants?: IIndexedSelectionItem[];
}

export interface IChatHistoryPage {
  data: IMessage[];
  hasNextPage: boolean;
}

export interface IChatListPage {
  data: IChatListObject[];
  hasNextPage: boolean;
}

export interface IHasMessageUuidInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IFindMessageInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IUpdateMessageContentInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
  content: string;
}

export interface IUpdateMessageInPagesProps {
  pages: IChatHistoryPage[];
  message: IMessageWithTempId;
}

export interface IAppendMessageDeltaProps {
  pages: IChatHistoryPage[];
  uuid: string;
  delta: string;
}

export interface IMessageToolCall {
  name: string;
  args: Record<string, unknown>;
  // NOTE: NSAI-5473 — set when this is a real call, which is then also a line
  // of the Steps list. A synthetic progress frame (presentation slides, the
  // tool-search loader) carries none, so its live row stays on screen.
  toolCallId?: string;
}

export interface ISetMessageToolCallProps {
  pages: IChatHistoryPage[];
  uuid: string;
  toolName: string;
  args: Record<string, unknown>;
  // NOTE: NSAI-5473 — set for a real tool call, which also becomes a line of
  // the message's step transcript.
  toolCallId?: string;
}

export interface IAppendMessageToolStepProps {
  message: IMessage;
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
}

export interface ISetMessageToolStepStatusProps {
  message: IMessage;
  toolCallId: string;
  status: EToolStepStatus;
}

export interface ISetMessageToolStepStatusInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
  toolCallId: string;
  status: EToolStepStatus;
}

export interface IResolveMessageToolCallProps {
  toolName: string;
  args: Record<string, unknown>;
  priorTool?: IMessageToolCall;
  toolCallId?: string;
}

export interface IAppendMessageReasoningProps {
  pages: IChatHistoryPage[];
  uuid: string;
  reasoning: string;
}

export interface ISetMessageStreamErrorProps {
  pages: IChatHistoryPage[];
  uuid: string;
  errorMessage: string;
}

export interface ISetMessageStreamFinishedProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface ISetMessageDeepSearchProgressProps {
  pages: IChatHistoryPage[];
  uuid: string;
  progress: IMessage["deepSearchProgress"];
  // NOTE: NSAI-6435 — the run's record when the event carries one.
  researchProgress?: IDeepResearchProgressSnapshot | undefined;
}

export interface ISetMessageWebSearchProgressProps {
  pages: IChatHistoryPage[];
  uuid: string;
  progress: IMessage["webSearchProgress"];
}

export interface ISetMessagePreparationProgressProps {
  pages: IChatHistoryPage[];
  uuid: string;
  progress: IMessage["preparationProgress"];
}

export interface ICancelSearchProgressForMessageInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface ICancelSearchProgressForChatInPagesProps {
  pages: IChatHistoryPage[];
}

export interface IUpsertNewMessagesIntoChatHistoryProps<TData> {
  oldData: TData | undefined;
  reversedMessages: IMessage[];
  lastMessageUuid: string;
}

export interface IApplyChatTitleGeneratedToChatListProps<TData = unknown> {
  oldData: TData;
  chat: IChatTitleGenerated;
}

// NOTE: NSAI-6766 — the open chat's own query (`[CHAT_QUERY_KEY, uuid]`) is a
// second copy of the title, next to the chat-list caches above, and it is the
// one the chat page's header renders.
export interface IApplyChatTitleGeneratedToChatProps<TData = unknown> {
  oldData: TData;
  chat: IChatTitleGenerated;
}

// NOTE: NSAI-6766 — the slices of TanStack Query's QueryClient that
// patchRestartingInFlightFetch and syncChatTitleGeneratedToChat need. Both
// apps' real client satisfies them structurally, so frontend-shared never
// imports @tanstack/react-query.
export interface IInFlightFetchQueryClientLike {
  isFetching: (filters: { queryKey: readonly unknown[] }) => number;
  cancelQueries: (filters: {
    queryKey: readonly unknown[];
  }) => Promise<unknown>;
  invalidateQueries: (filters: {
    queryKey: readonly unknown[];
  }) => Promise<unknown>;
}

export interface IChatQueryClientLike extends IInFlightFetchQueryClientLike {
  setQueryData: <TData extends IChatByUuidResponse>(
    queryKey: readonly unknown[],
    updater: (oldData: TData | undefined) => TData | undefined,
  ) => unknown;
}

// NOTE: NSAI-6766 — `patch` writes the cache synchronously; the helper decides
// whether an in-flight fetch under `queryKey` has to be cancelled before it
// and restarted after it.
export interface IPatchRestartingInFlightFetchProps {
  queryClient: IInFlightFetchQueryClientLike;
  queryKey: readonly unknown[];
  patch: () => void;
}

export interface ISyncChatTitleGeneratedToChatProps {
  queryClient: IChatQueryClientLike;
  chatQueryKey: readonly unknown[];
  chat: IChatTitleGenerated;
}

// NOTE: NSAI-6766 — syncChatTitleGenerated writes both copies of the title,
// the chat lists' and the chat's own, so it needs both slices.
export interface IChatTitleQueryClientLike
  extends IChatQueryClientLike, IChatListQueryClientLike {}

export interface ISyncChatTitleGeneratedProps {
  queryClient: IChatTitleQueryClientLike;
  chat: IChatTitleGenerated;
}

// NOTE: NSAI-4601 — patches the cached chat-list entry's lastMessageAt when a
// new message arrives over the socket, so the sidebar re-sorts/re-groups
// (getChatActivityDate / groupChatsByDate) without waiting for a full list
// refetch. Mirrors IApplyChatTitleGeneratedToChatListProps.
export interface IApplyLastMessageAtToChatListProps<TData = unknown> {
  oldData: TData;
  chatUuid: string;
  lastMessageAt: string;
}

// NOTE: NSAI-4601 — the minimal shape applyLastMessageAtToChatList needs from
// its caller's cache wrapper. InfiniteData<IChatListPage> (the real TanStack
// Query type both web and mobile pass in) satisfies this structurally, so
// frontend-shared never has to import @tanstack/react-query to share the
// function itself — only its own `pages` field is ever read or spread.
export interface IChatListPagesContainer {
  pages: IChatListPage[];
}

// NOTE: NSAI-4601 (PR review follow-up, Sonar new-code duplication) — the
// minimal slice of TanStack Query's QueryClient that syncChatListLastMessageAt
// needs. The real QueryClient (from both web's and mobile's own
// @tanstack/react-query import) satisfies this structurally, so
// frontend-shared never has to depend on that package to share the call-site
// itself, which used to be hand-copied into both hooks' handleNewMessage.
export interface IChatListQueryClientLike {
  setQueriesData: <TData extends IChatListPagesContainer>(
    filters: { queryKey: readonly unknown[] },
    updater: (oldData: TData | undefined) => TData | undefined,
  ) => unknown;
}

export interface ISyncChatListLastMessageAtProps {
  queryClient: IChatListQueryClientLike;
  chatListQueryKey: readonly unknown[];
  messages: Array<{ createdAt: string; generationType?: EGenerationType }>;
  chatUuid: string;
}

// NOTE: NSAI-4601 — the platform-agnostic half of patching a single chat's
// fields inside a cached IChatListPage[]. Shared so web and mobile don't each
// carry their own copy of the "find by uuid, rebuild only the touched page,
// preserve reference equality when unchanged" traversal (the two apps still
// own their own InfiniteData<IChatListPage> wrapper, since that TanStack
// Query type isn't a frontend-shared dependency).
export interface IPatchChatListPagesProps<TPatch> {
  pages: IChatListPage[];
  chatUuid: string;
  patch: TPatch;
  // NOTE: Optional pre-check run against the matching chat item before it is
  // patched — return false to leave it untouched (e.g. a monotonic-timestamp
  // guard so a delayed/out-of-order event can't regress an already-newer
  // cached value). Defaults to always patching when the chat is found.
  shouldPatch?: (chatItem: IChatListObject) => boolean;
}

export interface IPatchChatListPagesResult {
  pages: IChatListPage[];
  wasUpdated: boolean;
}

// NOTE: NSAI-4686 — the counterpart of IPatchChatListPagesProps for a chat that
// is LEAVING the list (moved to a folder, archived, unarchived) rather than
// being patched in place. No `shouldPatch` guard: unlike a timestamp write there
// is no out-of-order event to defend against — the caller removes the chat
// because the user just dropped it somewhere else.
export interface IRemoveChatFromListPagesProps {
  pages: IChatListPage[];
  chatUuid: string;
}

export interface IRemoveChatFromChatListProps<TData = unknown> {
  oldData: TData;
  chatUuid: string;
}

// NOTE: NSAI-4686 (PR review) — where a chat sat before it was optimistically
// removed, so a failed mutation can put that one row back instead of restoring
// the whole cached list from a snapshot. A snapshot restore also reverts
// everything that landed while the request was in flight (a page the user
// scrolled in, another drop that already succeeded); a single-row re-insert
// leaves all of it alone.
export interface IChatListRemoval {
  pageIndex: number;
  itemIndex: number;
  chat: IChatListObject;
}

export interface IFindChatListRemovalProps {
  pages: IChatListPage[];
  chatUuid: string;
}

export interface IRestoreChatToListPagesProps {
  pages: IChatListPage[];
  removal: IChatListRemoval;
}

export interface IRestoreChatToChatListProps<TData = unknown> {
  oldData: TData;
  removal: IChatListRemoval;
}

// NOTE: NSAI-4686 (PR review) — one cached list's worth of rollback: the query
// the row was taken out of, and where it sat. The three drop mutations each
// collect a list of these in onMutate and hand it back in onError.
export interface IChatListRowRemoval {
  queryKey: readonly unknown[];
  removal: IChatListRemoval;
}

export interface ICollectChatListRemovalsProps<TData> {
  cachedChatLists: [readonly unknown[], TData | undefined][];
  chatUuid: string;
}

export interface IRestoreChatListRowsProps {
  queryClient: IChatListRollbackQueryClientLike;
  rows: IChatListRowRemoval[];
}

// NOTE: the slice of TanStack Query's QueryClient the rollback needs, for the
// same reason as IChatListQueryClientLike above: both apps' real client
// satisfies it structurally, so frontend-shared never imports the package.
export interface IChatListRollbackQueryClientLike {
  setQueryData: <TData extends IChatListPagesContainer>(
    queryKey: readonly unknown[],
    updater: (oldData: TData | undefined) => TData | undefined,
  ) => unknown;
}

// NOTE: NSAI-4792 — the chat list is paginated by OFFSET, so any mutation that
// reorders it (pin, archive, unarchive, move to/from a folder, delete) can shift
// a chat across a page boundary while the already-loaded pages are refetched.
// Until every page has settled, two of them can both report the same chat, and a
// list keyed by chat uuid then renders two children with the same key.
export interface IDedupeChatsByUuidProps {
  chats: readonly IChatListObject[];
}

// NOTE: NSAI-4601 — guards applyLastMessageAtToChatList against clobbering a
// newer cached value with a stale one. handleNewMessage patches the sidebar
// with lastIncomingMessage.createdAt, but that isn't guaranteed to be the
// chat's true chats.last_message_at in every case a "newMessages" socket
// event can fire: a multi-model turn saves its assistant rows concurrently
// (Promise.all preserves input order, not save-completion order), and some
// emitters (e.g. the multi-model summary placeholder) emit "newMessages" for
// a row that never went through the server's own touchChatLastMessageAt
// write at all. The server guards its column write the same way
// (`last_message_at < :next`) — this mirrors that guard client-side.
export interface IIsIncomingChatTimestampNewerProps {
  currentTimestamp?: string | null;
  incomingTimestamp: string;
}

// NOTE: NSAI-4601 — a "newMessages" batch is not guaranteed to be sorted by
// createdAt. The server builds it as [user, ...assistantMessagesForClient],
// and for a multi-model turn the assistant rows come from Promise.all in
// producer/request order, not DB save-completion order — so the array's last
// element is not reliably the one with the latest createdAt. Picking the
// true max avoids caching a lastMessageAt that undercounts the turn's real
// latest activity (flagged in PR review: web/mobile use-chat-web-socket.ts).
//
// NOTE: NSAI-4601 (PR review follow-up) — generationType is optional so the
// helper still works with a minimal test double, but every real IMessage
// carries it. Summary and BranchContext rows are excluded from the timestamp
// scan entirely: see GENERATION_TYPES_WITHOUT_LAST_MESSAGE_AT_BUMP in
// chat-list-cache.ts for why their createdAt must not be treated as the
// chat's new activity timestamp.
export interface IGetLatestMessageTimestampProps {
  messages: Array<{ createdAt: string; generationType?: EGenerationType }>;
}

// NOTE: NSAI-5681 — see appendMessageToChatHistory on each client: a resolved
// connect card's transcript line, put into the cached history where the next
// read would have returned it.
export interface IAppendMessageToChatHistoryProps {
  pages: IChatHistoryPage[];
  message: IMessage;
}

export interface IApplyIntegrationConnectPromptToMessageProps {
  pages: IChatHistoryPage[];
  uuid: string;
  prompt: IIntegrationConnectPrompt;
}

export interface IApplyToolApprovalRespondedToMessageProps {
  message: IMessage;
  payload: IToolApprovalRespondedPayload;
}

export interface IUpsertToolApprovalRequestToMessageProps {
  message: IMessage;
  payload: IToolApprovalRequestPayload;
}

export interface ISendToolApprovalParams {
  approvalId: string;
  approved: boolean;
  reason?: string;
}

export interface IMessageStoppedPayload {
  chatUuid: string;
}

export interface IMessagesDeletedPayload {
  chatUuid: string;
  count: number;
}

export interface IKnowledgeSourcesPayload {
  chatUuid: string;
  uuid: string;
  sources?: Array<{
    content: string;
    score: number;
    fileName: string;
    fileUuid: string;
    source?: EKnowledgeSourceType;
  }>;
  // NOTE: Cross-chat context provenance (NSAI-3501) — emitted alongside
  // knowledge sources when prior chats' user-memory facts fed this answer.
  contextChatSources?: IContextChatSource[];
}

export interface IWebSocketError {
  message: string;
  error?: string;
  // NOTE: NSAI-4636 — set to MODEL_UNAVAILABLE_ERROR_CODE by the server only when
  // a send failed because the selected model is unavailable (disabled/hidden at
  // any tier), so clients can invalidate the model-picker caches. Absent for
  // every other socket error.
  errorCode?: string;
  // NOTE: NSAI-5042 — set when the failing send targeted a known chat, so
  // clients only roll back optimistic messages in that chat.
  chatUuid?: string;
  // NOTE: NSAI-5641 — true when the server also wrote this error into the
  // assistant message, so it is already visible in the transcript. Clients must
  // then skip the toast: the same paragraph in two places at once is harder to
  // read, and a transcript entry persists where a toast auto-dismisses before a
  // full paragraph can be read. The event itself still arrives, because clients
  // rely on it to clear optimistic temp messages and restore the draft.
  isRenderedInTranscript?: boolean;
}

// NOTE: NSAI-5042 — reason a buffered/optimistic send ultimately failed.
export type TSendFailureReason =
  | "expired"
  | "ack-failed"
  | "auth-refresh-failed"
  // NOTE: NSAI-5042 — the server definitively rejected the turn post-ack (a
  // gateway "error" event stripped the optimistic temps); raised client-side
  // so the composer restores the draft for a send that never persisted.
  | "rejected"
  | "stalled";

// NOTE: NSAI-5042 — payload for the client-side send-failure channel
// (WebSocketService.onSendFailure). content carries the original user text so
// the UI can restore the draft instead of losing the input.
export interface ISendFailurePayload {
  chatId: string | null;
  content: string;
  reason: TSendFailureReason;
}

export interface IMessageFile {
  type: string;
  name?: string;
  // NOTE: S3 object key — set when file was uploaded via presigned URL.
  s3Key?: string;
  // NOTE: UUID of an existing message_files row. Used for resend — backend resolves to s3Key internally.
  fileUuid?: string;
}

export interface IMessagePayload {
  chatId?: string;
  content: string;
  role: EMessageRole;
  models?: IIndexedSelectionItem[];
  assistants?: IIndexedSelectionItem[];
  files?: IMessageFile[];
  searchMode: ESearchMode;
  requestId?: string;
  isRetry?: boolean;
  // NOTE: NSAI-3308 — the skills the user pinned to THIS message. Present on the
  // buffered payload as well as the outgoing one, so a reconnect replay pins the
  // same skills as the original send.
  skillIds?: string[];
  // NOTE: NSAI-5925 — the mentions the per-message cap refused. Buffered for the
  // same reason `skillIds` is: a replay must state the same refusals.
  refusedSkillNames?: string[];
}

// NOTE: React Native FormData accepts {uri, type, name} objects for file uploads
export interface IReactNativeFileUri {
  uri: string;
  type: string;
  name: string;
}

export interface ITranscribeAudioRequest {
  audio: Blob | IReactNativeFileUri;
  fileName?: string;
}

export interface ITranscribeAudioResponse {
  text: string;
}

export interface ISendMessagePayload {
  chatId?: string;
  content: string;
  role: EMessageRole;
  models?: IIndexedSelectionItem[];
  assistants?: IIndexedSelectionItem[];
  files?: IMessageFile[];
  searchMode: ESearchMode;
  reasoningMode?: EReasoningMode;
  // NOTE: Per-generation override for the org presentation brand theme. When
  // false, the brand theme is skipped for presentations generated this turn.
  applyPresentationTheme?: boolean;
  // NOTE: NSAI-3308 — stable skill uuids the user pinned to THIS message, never
  // skill instructions. The bound is a UI guard: the server re-authorizes every
  // uuid through the same resolver the model paths use.
  skillIds?: string[];
  // NOTE: NSAI-5925 — names of the skills this message mentioned and the cap
  // refused, so the sent message can record the refusal the composer showed. The
  // server normalizes them again; the gateway does not validate socket payloads.
  refusedSkillNames?: string[];
  // NOTE: NSAI-6541 — set when this send overrides a capability Auto enabled
  // (a one-click re-run without it, or switching it off in the composer). The
  // server only counts it; the turn itself follows `searchMode` /
  // `reasoningMode` as usual.
  autoOverride?: IAutoCapabilityOverride;
}

export interface IRequestMultiModelSummaryPayload {
  chatId: string;
  messageUuids: string[];
  // NOTE: NSAI-6445 — the member's OWN summary model, null when they have none.
  // Never fill it with a default: the server treats any uuid here as the
  // member's choice, so a client-side default hides the organization's Summary
  // model and the deployment default behind it.
  summaryModelUuid: string | null;
}

export interface IImprovePromptPayload {
  requestId: string;
  prompt: string;
  // NOTE: NSAI-6242 — uuid of the chat this draft belongs to, so the server can
  // resolve what the draft leaves implicit ("shorter", "the second point")
  // against the real conversation instead of rewriting it as if it were the
  // first message of a new chat. Optional because a draft in a chat that has not
  // been created yet has no uuid; that case improves with no context, as before.
  // The client sends the ID ONLY — the transcript is read server-side under the
  // caller's own user + project scope.
  chatId?: string;
}

export interface IPromptImproveDeltaPayload {
  requestId: string;
  delta: string;
}

export interface IPromptImproveFinishPayload {
  requestId: string;
  improvedPrompt: string;
}

export interface IPromptImproveErrorPayload {
  requestId: string;
  error: string;
  // NOTE: NSAI-2721 (review follow-up) — present only when the rejection is
  // a per-user rate-limit / cooldown bounce. The frontend uses this signal
  // to show a warning-style toast (rate limit is UX, not an outage) and to
  // disable the Improve button with a live countdown until the cap clears.
  // Absent / undefined for real errors (model failure, PII gateway down,
  // socket disconnect, etc.) — those still surface as error-style toasts.
  retryAfterSeconds?: number;
}

export interface IPresignFileUploadParams {
  fileName: string;
  contentType: string;
  // NOTE: NSAI-4264 — optional declared size, checked server-side as a cheap
  // early rejection before the S3 round trip. Not authoritative on its own.
  fileSizeBytes?: number;
  // NOTE: AbortSignal to cancel the presign request before upload starts
  signal?: AbortSignal;
}

export interface IPresignFileUploadResponse {
  uploadUrl: string;
  s3Key: string;
}

export interface IDownloadProgressEvent {
  loaded: number;
  total: number | undefined;
}

export interface IDownloadFileParams {
  fileUuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
  // NOTE: NSAI-5312 — lets a one-file download started from a generated-files
  // selection be cancelled like the archive it stands in for.
  signal?: AbortSignal;
  // NOTE: NSAI-5660 — set when the bytes are read to render a generated HTML
  // page in the preview, so the read is audited as a preview, not a download.
  purpose?: TChatFileReadPurpose;
}

// NOTE: NSAI-5660 — why a chat file's bytes are read. Sent as the
// CHAT_FILE_READ_PURPOSE_HEADER header, so the URL and its cache entry stay the
// same as a plain download.
export type TChatFileReadPurpose = "preview";

export interface IDownloadPreviewFileParams {
  fileUuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
}

// NOTE: NSAI-7566 — a share recipient downloads through the share-token route,
// which reports progress exactly like the owner's `downloadFile`.
export interface IDownloadSharedChatFileParams {
  shareToken: string;
  fileUuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
}

export interface IGetFilePreviewSlidesParams {
  fileUuid: string;
}

// NOTE: Per-slide preview images for a generated presentation file (NSAI-4418).
// A .pptx has no in-app renderer on React Native, so the server rasterizes the
// deck to images and mobile shows them as a swipeable slide gallery. Each entry
// is a ready-to-render image data URL (e.g. "data:image/jpeg;base64,…").
export interface IFilePreviewSlidesResponse {
  slides: string[];
  slideCount: number;
}

export interface IClearMessageStreamErrorParams {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IPendingMessage {
  payload: IMessagePayload;
  timestamp: number;
  // NOTE: NSAI-5042 — flush retries already made for this entry; compared
  // against PENDING_MESSAGE_MAX_ATTEMPTS by decidePendingSend.
  attempts: number;
}

export interface IWebSocketServiceConfig {
  url: string;
  storageAdapter: IStorageAdapter;
  // NOTE: NSAI-4330 — when set, read on every `connect()` instead of `url`, so
  // a deployment the user switched to at runtime (mobile "Change tenant") is
  // honoured by the next socket without recreating the singleton.
  getUrl?: () => string;
  getLanguage?: () => string | null;
  getProjectUuid?: () => string | null;
  onTokenRefresh?: () => Promise<string | null>;
  // NOTE: Optional getter for E2E test scenario header
  getTestScenario?: () => string | null;
  // NOTE: NSAI-5909 — applies whatever the platform's stream buffer has parked.
  // Per-token frames are coalesced client-side, so every OTHER socket event
  // must see them applied first: a `streamFinish` or `messageUpdated` that
  // overtook a parked delta would be undone when the delta finally landed.
  // Called here rather than from each consumer because `onAny` listeners run
  // ahead of every named listener, which is the only place that ordering can
  // be guaranteed for the chat hook and the background trackers alike.
  onFlushStreamBuffer?: () => void;
  // NOTE: NSAI-5117 — true while an assistant reply is actively streaming for
  // any chat this client cares about. Supplying it turns on the deferred auth
  // reconnect: a token refresh mid-stream updates `socket.auth` but does NOT
  // cycle the handshake until the stream settles, so the in-flight (often
  // still-empty) assistant message is not dropped. Omitted ⇒ reconnect
  // immediately, which is the behaviour of a client that never defers.
  hasActiveStream?: () => boolean;
  // NOTE: NSAI-5117 — lets a platform keep its own request-id generator. The
  // web client uses `crypto.randomUUID()`; omitted ⇒ the built-in generator.
  generateRequestId?: () => string;
  // NOTE: NSAI-5117 — runs before a send is emitted and may throw to abort it.
  // Exists for the web E2E hook that forces a synchronous send failure.
  beforeSendMessage?: () => void;
  // NOTE: NSAI-5117 — E2E-only timing compression. Each returns undefined
  // outside a test run, so production always uses the real constants.
  getAckTimeoutMsOverride?: () => number | undefined;
  getHealthCheckTimeoutMsOverride?: () => number | undefined;
  // NOTE: NSAI-5117 — the gateway rejected a guarded event because the licence
  // expired. The web client stores a return path and navigates to its
  // licence-expired route; a client that omits this just logs the exception.
  onLicenseExpired?: () => void;
  // NOTE: NSAI-5910 — whether the app is in front of the user. A handshake
  // refused again after a token refresh is retried on a doubling timer while
  // it is, and only once it is again while it is not. Omitted ⇒ always in
  // front (timer only).
  activitySource?: IAppActivitySource;
}

export interface IClearMessageStreamErrorProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IPendingFile {
  type: string;
  name?: string;
  s3Key?: string;
  // NOTE: UUID of an existing file. Used for resend — backend resolves to s3Key internally.
  fileUuid?: string;
}

export interface IModelMeta {
  title: string;
  provider?: string;
}

export interface IGetModelByIdParams {
  models: IModel[];
  id: string;
  index?: number;
}

export interface IGetAssistantByIdParams {
  assistants: IAssistant[];
  id: string;
  index?: number;
}

export interface IReplaceSelectedModelParams {
  selectedModels: IIndexedSelectionItemModel[];
  models: IModel[];
  // NOTE: Null when the picker has no replace target; the helper then no-ops.
  targetUuid: string | null;
  nextUuid: string;
}

export interface IReplaceSelectedAssistantParams {
  selectedAssistants: IIndexedSelectionItemAssistant[];
  assistants: IAssistant[];
  // NOTE: Null when the picker has no replace target; the helper then no-ops.
  targetUuid: string | null;
  nextUuid: string;
}

export interface IBaseModelOrAssistantForUpdateChat {
  uuid: string;
  index: number;
}

export interface IGetTruncatedDisplayNameParams {
  text: string;
  maxLength: number;
}

export interface IGetTruncatedDisplayNameResult {
  displayName: string;
  isTruncated: boolean;
}

interface IPayloadFile {
  name: string;
  type: string;
}

export interface IGetApproxSocketPayloadSizeBytesProps {
  messageContent: string;
  files: IPayloadFile[];
  extraOverheadBytes?: number;
  perFileOverheadBytes?: number;
}

export enum EMicState {
  Idle = "idle",
  Recording = "recording",
  Transcribing = "transcribing",
}

export enum EAudioTranscriptionDisclaimerDecision {
  Agreed = "agreed",
  Cancelled = "cancelled",
}

export enum EBlockKind {
  ModelChange = "modelChange",
  Single = "single",
  Row = "row",
  Disclaimer = "disclaimer",
}

export type TBlock =
  | {
      kind: EBlockKind.ModelChange;
      key: string;
      models: IModelForHistory[];
      previousModels: IModelForHistory[];
      modelUuids: string[];
      isOriginalModelSet: boolean;
      showResetModelButton: boolean;
    }
  | { kind: EBlockKind.Single; key: string; message: IMessage }
  | {
      kind: EBlockKind.Row;
      key: string;
      userMessage: IMessage;
      children: IMessage[];
    }
  // NOTE: NSAI-5978 — a synthetic row carrying the AI disclaimer, inserted once
  // after a chat's first AI answer. Mobile only: web keeps its caption under the
  // composer, where a single line costs nothing, so its own block builder never
  // emits this kind.
  | { kind: EBlockKind.Disclaimer; key: string };

export interface ICollectSummarySourceUuidsProps {
  messages: IMessage[];
  assistantMessageIndex: number;
}

export interface IFindLastRetryableSummaryUuidProps {
  renderedBlocks: TBlock[];
}

export interface IGetSummaryButtonVisibilityProps {
  blocksAfter: TBlock[];
  children: IMessage[];
  chatId?: string;
}

export interface IGetSummaryButtonVisibilityResult {
  shouldShow: boolean;
  areAllChildrenSettled: boolean;
  summarizableMessageUuids: string[];
}

// NOTE: Cross-chat semantic search request — `q` mirrors the server-side
// DTO field name (`@Query()` reads `q` from the URL). Limit is the page
// size cap; both fields are optional from the FE side but the API enforces
// a length check on `q`.
export interface ISearchAcrossChatsParams {
  q: string;
  limit?: number;
}

// NOTE: A single semantic search hit shared between the web and mobile
// clients. `matchedAt` arrives as an ISO string from the JSON wire, but
// React Query consumers re-parse to Date downstream; keeping it as string
// here matches the actual payload bytes. `projectName` is the owning
// project's title (cross-chat search is org-scoped, so a hit can belong
// to a project other than the active one); null when the project row
// could not be hydrated server-side (concurrent soft-delete).
// `projectUuid` (NSAI-6720) lets the client switch into that project before
// opening the chat — a chat is only readable inside its own project — and
// `isArchived` marks a hit whose chat is archived (still searchable, opens
// read-only).
export interface ICrossChatSearchHit {
  chatUuid: string;
  chatTitle: string | null;
  projectName: string | null;
  projectUuid: string | null;
  isArchived: boolean;
  messageUuid: string;
  snippet: string;
  score: number;
  matchedAt: string;
}

export type TCrossChatSearchResponse = ICrossChatSearchHit[];

// NOTE: NSAI-5908 — the structural slice of TanStack's
// InfiniteData<IChatHistoryPage> that the copy-on-write cache helpers need.
// Same reasoning as IChatListPagesContainer above: frontend-shared never
// imports @tanstack/react-query, and both apps' real InfiniteData satisfies
// this shape structurally.
export interface IChatHistoryPagesContainer {
  pages: IChatHistoryPage[];
}

export interface IMessageLocationInPages {
  pageIndex: number;
  messageIndex: number;
}

export interface IFindMessageLocationInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

// NOTE: NSAI-5908 — what a message mutator receives from
// updateMessageInChatHistory: the cloned message it may mutate in place, and
// the rebuilt pages array that already holds that clone, so the existing
// `pages`-based helpers (appendMessageDelta, setMessageToolCall, …) keep
// working unchanged — they find the clone by uuid and mutate it.
export interface IMutateChatHistoryMessageProps {
  message: IMessage;
  pages: IChatHistoryPage[];
}

export interface IUpdateMessageInChatHistoryProps<
  TData extends IChatHistoryPagesContainer,
> {
  oldData: TData;
  uuid: string;
  // NOTE: Platform clone for ONE message — web passes structuredClone, mobile
  // its deepClone. A deep copy (not a spread) so a mutator that pushes into a
  // nested array can never write through into the cached original.
  cloneMessage: (message: IMessage) => IMessage;
  // NOTE: Returns whether anything changed; `false` hands the untouched
  // `oldData` back so observers are not notified for a no-op.
  mutate: (props: IMutateChatHistoryMessageProps) => boolean;
}

export interface IUpdateFirstPageInChatHistoryProps<
  TData extends IChatHistoryPagesContainer,
> {
  oldData: TData;
  // NOTE: Returns the next `data` array for the first page, or `null` to
  // leave the cache untouched (same reference out).
  update: (data: IMessage[]) => IMessage[] | null;
}

export interface IMapMessagesInChatHistoryProps<
  TData extends IChatHistoryPagesContainer,
> {
  oldData: TData;
  // NOTE: Returns a replacement message, or `null` to keep the original
  // reference. Only pages holding a replaced message are rebuilt.
  map: (message: IMessage) => IMessage | null;
}

// NOTE: NSAI-5908 — text and reasoning streamed for one message since the
// last flush. Deltas are concatenated in arrival order, so applying the chunk
// is indistinguishable from applying every frame one by one.
export interface IPendingMessageStreamChunk {
  // NOTE: Deltas that arrived after the latest full replacement (or since the
  // last drain when there was none), in arrival order.
  content: string;
  reasoning: string;
  // NOTE: The text of the latest `messageContentUpdate` — a full replacement
  // that supersedes every delta parked before it.
  replacementContent?: string;
}

// NOTE: chatUuid → (message uuid → pending chunk).
export type TPendingStreamChunkBuffer = Map<
  string,
  Map<string, IPendingMessageStreamChunk>
>;

export interface IEnqueueStreamChunkProps {
  buffer: TPendingStreamChunkBuffer;
  chatUuid: string;
  uuid: string;
  delta?: string;
  reasoning?: string;
  replacementContent?: string;
}

export interface IDrainStreamChunksProps {
  buffer: TPendingStreamChunkBuffer;
  // NOTE: Omit to drain every chat.
  chatUuid?: string;
}

export interface IDrainedStreamChunks {
  chatUuid: string;
  chunks: Map<string, IPendingMessageStreamChunk>;
}

export interface IHasPendingStreamChunksProps {
  buffer: TPendingStreamChunkBuffer;
  chatUuid?: string;
}
